import { message } from 'antd';
import { ServerUploadFile } from 'components/common/upload/ServerUpload';
import { findIndex } from 'lodash';
import { useDeleteFileMutation, useUpdateFileMutation, useUploadFileMutation } from 'services';
import { FileUpload, IconItem } from 'types';
import { FilesBucketNames } from 'utils';
import { isRcFile, isServerUploadFile } from 'utils/upload';

export function useUploadForm() {
  const [onUpload, { isLoading }] = useUploadFileMutation();
  const [onDelete, { isLoading: isLoadingDelete }] = useDeleteFileMutation();
  const [onUpdate, { isLoading: isLoadingUpdate }] = useUpdateFileMutation();

  const onUploadFiles = async (files: Array<FileUpload | ServerUploadFile>): Promise<number[]> => {
    const uploadPromises = Array.from(files).map((file) => {
      if (!file) return null;
      if (isServerUploadFile(file)) return file.fileId;
      if ('originFileObj' in file && file.originFileObj) {
        return onUpload({
          file: file.originFileObj,
          bucketName: FilesBucketNames.PUBLIC
        })
          .unwrap()
          .then((rs) => rs.data?.fileId!)
          .catch((err) => message.error(err));
      }
      if (isRcFile(file) || file instanceof File) {
        return onUpload({
          file: file,
          bucketName: FilesBucketNames.PUBLIC
        })
          .unwrap()
          .then((rs) => rs.data?.fileId!)
          .catch((err) => message.error(err));
      }

      return 0;
    });
    try {
      const responses = await Promise.all(uploadPromises);
      return responses.filter((fileId) => fileId && typeof fileId === 'number').map((fileId) => fileId as number);
    } catch (error) {
      return [];
    }
  };

  const onReplaceFiles = async (
    files: Array<(FileUpload & { fileId?: number }) | ServerUploadFile>
  ): Promise<number[]> => {
    const uploadPromises = Array.from(files).map((file) => {
      if (!file) return null;
      if ('originFileObj' in file && file.originFileObj) {
        if ('fileId' in file && file.fileId) {
          return onUpdate({
            file: file.originFileObj,
            fileId: file.fileId
          })
            .unwrap()
            .then((rs) => rs.data?.fileId!)
            .catch((err) => message.error(err));
        }
        return onUpload({
          file: file.originFileObj,
          bucketName: FilesBucketNames.PUBLIC
        })
          .unwrap()
          .then((rs) => rs.data?.fileId!)
          .catch((err) => message.error(err));
      }
      if (isRcFile(file) || file instanceof File) {
        if ('fileId' in file && file.fileId) {
          return onUpdate({
            file: file,
            fileId: file.fileId
          })
            .unwrap()
            .then((rs) => rs.data?.fileId!)
            .catch((err) => message.error(err));
        }
        return onUpload({
          file: file,
          bucketName: FilesBucketNames.PUBLIC
        })
          .unwrap()
          .then((rs) => rs.data?.fileId!)
          .catch((err) => message.error(err));
      }
      if (isServerUploadFile(file)) return file.fileId;

      return 0;
    });
    try {
      const responses = await Promise.all(uploadPromises);
      return responses.filter((fileId) => fileId && typeof fileId === 'number').map((fileId) => fileId as number);
    } catch (error) {
      return [];
    }
  };

  const onDeleteFiles = async (files: number[]) => {
    for (const item of files) {
      try {
        await onDelete(item).unwrap();
      } catch (error) {}
    }
  };

  const handleMultiUpload = async (
    uploadFiles: Array<FileUpload | ServerUploadFile>,
    originFiles?: number[]
  ): Promise<number[]> => {
    if (originFiles && originFiles.length > 0) {
      const deleteFiles = originFiles.filter((file) => findIndex(uploadFiles, { fileId: file }) === -1);
      if (deleteFiles.length > 0) {
        await onDeleteFiles(deleteFiles);
      }
    }
    if (uploadFiles) {
      return await onUploadFiles(uploadFiles);
    }
    return [];
  };

  const handleMultiUploadAndReplace = async (
    uploadFiles?: Array<FileUpload | ServerUploadFile>,
    originFiles?: number[]
  ) => {
    if (!uploadFiles) return [];
    const remainingArray = originFiles?.filter((file) => findIndex(uploadFiles, { fileId: file }) === -1) || [];
    const mappedUploadArray = uploadFiles.map((file) => {
      // Nếu phần tử đã có id, bỏ qua việc gán
      if (isServerUploadFile(file) && file.fileId) {
        return file;
      }

      // Nếu array2 còn phần tử, gán id và xóa phần tử khỏi remainingArray
      const nextId = remainingArray.shift() ?? 0;
      return { ...file, fileId: nextId };
    });

    if (remainingArray.length) {
      await onDeleteFiles(remainingArray);
    }
    if (mappedUploadArray.length) {
      return await onReplaceFiles(mappedUploadArray);
    }

    return [];
  };

  const handleUploadIcon = async (icon: IconItem, deletedId?: number) => {
    try {
      const fileResponse = await fetch(icon.path);
      const blob = await fileResponse.blob();
      const file = new File([blob], icon.key, { type: blob.type });
      const uploadResponse = await onUpload({
        bucketName: FilesBucketNames.PUBLIC,
        file
      }).unwrap();
      if (deletedId) onDelete(deletedId).unwrap();
      return uploadResponse;
    } catch (err) {
      console.error(err);
    }
  };

  const handleReplaceIcon = async (icon: IconItem, replaceId: number) => {
    try {
      const fileResponse = await fetch(icon.path);
      const blob = await fileResponse.blob();
      const file = new File([blob], icon.key, { type: blob.type });
      const uploadResponse = await onUpdate({
        file,
        fileId: replaceId
      }).unwrap();

      return uploadResponse;
    } catch (err) {
      console.error(err);
    }
  };

  return {
    handleMultiUpload,
    handleUploadIcon,
    isLoading: isLoading || isLoadingDelete || isLoadingUpdate,
    handleMultiUploadAndReplace,
    handleReplaceIcon
  };
}
